import { filterTypesEnum, type FilterOption } from "@/types/common/filters";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  TableData,
  useToast,
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "@elaniintc/goes-design-system";
import {
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { parseAsString, useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useDebounceValue } from "usehooks-ts";
import { FiltersModal } from "@/components/ui/filters/FiltersModal";
import TableHeader from "@/components/common/table/header";
import Pagination from "@/components/common/table/pagination";
import {
  useGetTags,
  useDeleteTag,
  useUpdateTag,
} from "@/queries/tags/tags.api";
import type { TagType } from "@/types/entities/tags";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import ConfirmationModalBase from "@/components/common/modals/confirmation-modal-base";
import NewTagModal from "./NewTagModal";

// Map API active boolean to display status
const mapActiveToDisplay = (active: boolean): "Activa" | "Inactiva" => {
  return active ? "Activa" : "Inactiva";
};

// Map display status to API active boolean
const mapDisplayToActive = (status: string): boolean => {
  return status === "Activa";
};

// Helper function to convert filter status to API parameters
const getActiveFilter = (status: string) => {
  if (status.includes(",")) {
    // Si hay múltiples estados seleccionados, no aplicar filtro
    const statusArray = status.split(",").map((s) => s.trim());
    const hasActiva = statusArray.includes("Activa");
    const hasInactiva = statusArray.includes("Inactiva");

    if (hasActiva && hasInactiva) {
      // Si ambos están seleccionados, no filtrar
      return {};
    } else if (hasActiva) {
      return { filter: { active: 1 } };
    } else if (hasInactiva) {
      return { filter: { active: 0 } };
    }
  } else {
    // Solo un estado seleccionado
    return { filter: { active: mapDisplayToActive(status) ? 1 : 0 } };
  }
  return {};
};

const columnHelper = createColumnHelper<TagType>();

const Menu = ({
  tag,
  onEdit,
  onToggleStatus,
  onDelete,
  onViewHistory,
}: {
  tag: TagType;
  onEdit: (tag: TagType) => void;
  onToggleStatus: (tag: TagType) => void;
  onDelete: (tag: TagType) => void;
  onViewHistory: (tag: TagType) => void;
}) => {
  const { hasPermission } = useAuthorization();
  const canEdit = hasPermission(Permissions.tags.update);
  const canDelete = hasPermission(Permissions.tags.delete);

  if (!canEdit && !canDelete) return null;

  const isActive = tag.active;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="xs" className="size-8">
          <Icon
            type="far"
            name="ellipsis-vertical"
            className="text-current text-sm"
          />
          <span className="sr-only">Acciones para etiqueta</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[222px] min-w-56 rounded-lg border-neutral-silent text-body font-normal p-2"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <DropdownMenuGroup>
          {/* Opción de ver historial */}
          <DropdownMenuItem
            className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
            onClick={() => onViewHistory(tag)}
          >
            Ver historial
          </DropdownMenuItem>

          {/* Opción de editar */}
          {canEdit && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={() => onEdit(tag)}
            >
              Editar etiqueta
            </DropdownMenuItem>
          )}

          {/* Toggle status */}
          {isActive ? (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={() => onToggleStatus(tag)}
            >
              Desactivar etiqueta
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={() => onToggleStatus(tag)}
            >
              Activar etiqueta
            </DropdownMenuItem>
          )}

          {/* Eliminar */}
          {canDelete && (
            <DropdownMenuItem
              className={`hover:bg-neutral-object-accent-silent py-2 px-3 ${
                tag.processes_count > 0
                  ? "text-neutral-quiet cursor-not-allowed opacity-50"
                  : "text-danger cursor-pointer"
              }`}
              onClick={() => {
                if (tag.processes_count === 0) {
                  onDelete(tag);
                }
              }}
              disabled={tag.processes_count > 0}
            >
              {tag.processes_count > 0
                ? `No se puede eliminar (${tag.processes_count} proceso${tag.processes_count !== 1 ? "s" : ""} asociado${tag.processes_count !== 1 ? "s" : ""})`
                : "Eliminar etiqueta"}
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const TagsList = () => {
  const { hasPermission } = useAuthorization();
  const canCreate = hasPermission(Permissions.tags.create);
  const canEdit = hasPermission(Permissions.tags.update);
  const canDelete = hasPermission(Permissions.tags.delete);
  const navigate = useNavigate();

  const [selectedTag, setSelectedTag] = useState<TagType | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isActivateTagModalOpen, setIsActivateTagModalOpen] = useState(false);
  const [isDeactivateTagModalOpen, setIsDeactivateTagModalOpen] =
    useState(false);
  const [isNewTagModalOpen, setIsNewTagModalOpen] = useState(false);
  const [isEditTagModalOpen, setIsEditTagModalOpen] = useState(false);

  const { showToast } = useToast();

  const [sorting, setSorting] = useState<SortingState>([]);

  const [page, setPage] = useState<number>(1);
  const [query, setQuery] = useQueryState("search", parseAsString);
  const [status, setStatus] = useQueryState("status", parseAsString);
  const [updated_at, setUpdatedAt] = useQueryState("updated_at", parseAsString);
  const [order_by, setOrderBy] = useQueryState(
    "order_by",
    parseAsString.withDefault("!created_at"),
  );
  const [searchInput, setSearchInput] = useState<string | null>(query || "");
  const [deferredQuery] = useDebounceValue(searchInput || "", 500);
  const [modalState, setModalState] = useState<"idle" | "filters">("idle");

  // Función para inicializar filtros basados en la URL
  const initializeFilters = useCallback(() => {
    const initialFilters: FilterOption[] = [
      {
        id: 1,
        field: "updated_at",
        type: filterTypesEnum.dates,
        label: "Fecha de última actualización",
        isUpdated: !!updated_at,
        shouldDisabledFutureDates: true,
        result: updated_at || "",
      },
      {
        id: 2,
        field: "status",
        type: filterTypesEnum.status,
        label: "Estado de la etiqueta",
        isUpdated: !!status,
        result: status || "",
        status: [
          {
            field: "Activa",
            label: "Activa",
            value: status?.includes("Activa") || false,
          },
          {
            field: "Inactiva",
            label: "Inactiva",
            value: status?.includes("Inactiva") || false,
          },
        ],
      },
    ];
    return initialFilters;
  }, [status, updated_at]);

  const [formatFilters, setFormatFilters] = useState<FilterOption[]>([]);

  // Inicializar filtros cuando el componente se monta o cambian los parámetros de URL
  useEffect(() => {
    const filters = initializeFilters();
    setFormatFilters(filters);
  }, [initializeFilters]);

  useEffect(() => {
    setQuery(deferredQuery || null);
    setPage(1);
  }, [deferredQuery, setQuery, setPage]);

  // Get tags from API
  const buildFilterParams = () => {
    const filterObj: { active?: number; search?: string; updated_at?: string } =
      {};

    // Agregar filtro de búsqueda si existe
    if (deferredQuery) {
      filterObj.search = deferredQuery;
    }

    // Agregar filtro de fecha de actualización si existe
    if (updated_at) {
      filterObj.updated_at = updated_at;
    }

    // Agregar filtro de estado si existe
    if (status) {
      const statusFilter = getActiveFilter(status);
      if (statusFilter.filter?.active !== undefined) {
        filterObj.active = statusFilter.filter.active;
      }
    }

    return Object.keys(filterObj).length > 0 ? { filter: filterObj } : {};
  };

  const apiParams = {
    page,
    per_page: 20,
    relationships: "processes,files",
    ...buildFilterParams(),
    ...(order_by ? { order_by } : {}),
  };

  const { data, isFetching } = useGetTags(apiParams);

  const shouldShowSkeleton = !data;

  // Mutations
  const deleteMutation = useDeleteTag();
  const updateMutation = useUpdateTag();

  // Action handlers
  const handleEditTag = useCallback((tag: TagType) => {
    setSelectedTag(tag);
    setIsEditTagModalOpen(true);
  }, []);

  const handleDeleteTag = useCallback((tag: TagType) => {
    setSelectedTag(tag);
    setIsDeleteModalOpen(true);
  }, []);

  const handleToggleStatus = useCallback((tag: TagType) => {
    setSelectedTag(tag);
    if (tag.active) {
      setIsDeactivateTagModalOpen(true);
    } else {
      setIsActivateTagModalOpen(true);
    }
  }, []);

  const handleCellButtonClick = useCallback((tag: TagType) => {
    setSelectedTag(tag);
    setIsEditTagModalOpen(true);
  }, []);

  const handleViewHistoryTag = useCallback(
    (tag: TagType) => {
      // Navigate to tag details page with history tab
      navigate(`/procesos/etiquetas/${tag.id}?type=historial`);
    },
    [navigate],
  );

  // Modal handlers
  const handleTagSuccess = () => {
    showToast({
      title: "Nueva etiqueta creada",
      description:
        "La etiqueta se guardó correctamente. Puedes editarla o eliminarla en cualquier momento.",
      options: {
        type: "success",
        duration: 4000,
        customIcon: (
          <Avatar size="xs" shape="square">
            <AvatarFallback color="action" shape="square">
              <Icon type="fas" name="tags" />
            </AvatarFallback>
          </Avatar>
        ),
      },
    });
  };

  const handleTagEditSuccess = () => {
    // No mostrar toast aquí ya que el NewTagModal ya maneja el toast de edición
  };

  const handleCloseNewTagModal = () => {
    setIsNewTagModalOpen(false);
    setSelectedTag(null);
  };

  const handleCloseEditTagModal = () => {
    setIsEditTagModalOpen(false);
    setSelectedTag(null);
  };

  // const handleViewHistoryFromModal = (tagId: string) => {
  //   // Navigate to tag details page with history tab
  //   navigate(`/procesos/etiquetas/${tagId}?type=historial`);
  // };

  const confirmDelete = () => {
    if (selectedTag) {
      // Verificar si el tag tiene procesos asociados
      if (selectedTag.processes_count > 0) {
        showToast({
          title: "No se puede eliminar",
          description: `Esta etiqueta no puede ser eliminada porque tiene ${selectedTag.processes_count} proceso${selectedTag.processes_count !== 1 ? "s" : ""} asociado${selectedTag.processes_count !== 1 ? "s" : ""}. Primero desasocia todos los procesos.`,
          options: {
            type: "warning",
            duration: 6000,
            customIcon: (
              <Avatar size="xs" shape="square">
                <AvatarFallback color="warning" shape="square">
                  <Icon type="fas" name="triangle-exclamation" />
                </AvatarFallback>
              </Avatar>
            ),
          },
        });
        setIsDeleteModalOpen(false);
        setSelectedTag(null);
        return;
      }

      deleteMutation.mutate(selectedTag.id, {
        onSuccess: () => {
          showToast({
            title: "Etiqueta eliminada",
            description: "La etiqueta fue eliminada exitosamente.",
            options: {
              type: "error",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="danger" shape="square" type={"ghost"}>
                    <Icon type="fas" name="tag" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
          setIsDeleteModalOpen(false);
          setSelectedTag(null);
        },
        onError: () => {
          showToast({
            title: "Error al eliminar",
            description:
              "La etiqueta no pudo ser eliminada. Por favor inténtalo nuevamente.",
            options: {
              type: "error",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="danger" shape="square">
                    <Icon type="fas" name="exclamation-triangle" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
        },
      });
    }
  };

  const confirmToggleStatus = () => {
    if (selectedTag) {
      const newActiveStatus = !selectedTag.active;
      const isActivating = newActiveStatus;

      updateMutation.mutate(
        {
          id: selectedTag.id,
          payload: {
            active: newActiveStatus,
          },
        },
        {
          onSuccess: () => {
            if (isActivating) {
              showToast({
                title: "Etiqueta activada",
                description: "La etiqueta fué activada exitosamente.",
                options: {
                  type: "success",
                  duration: 4000,
                  customIcon: (
                    <Avatar size="xs" shape="square">
                      <AvatarFallback color="success" shape="square">
                        <Icon type="fas" name="circle-check" />
                      </AvatarFallback>
                    </Avatar>
                  ),
                },
              });
            } else {
              showToast({
                title: "Etiqueta desactivada",
                description: "La etiqueta fue desactivada exitosamente.",
                options: {
                  type: "success",
                  duration: 4000,
                  customIcon: (
                    <Avatar size="xs" shape="square">
                      <AvatarFallback color="warning" shape="square">
                        <Icon type="fas" name="circle-minus" />
                      </AvatarFallback>
                    </Avatar>
                  ),
                },
              });
            }
            if (isActivating) {
              setIsActivateTagModalOpen(false);
            } else {
              setIsDeactivateTagModalOpen(false);
            }
            setSelectedTag(null);
          },
          onError: () => {
            showToast({
              title: isActivating ? "Error al activar" : "Error al desactivar",
              description: `Los cambios de la etiqueta no pudieron ser ${isActivating ? "activados" : "desactivados"}. Por favor inténtalo de nuevo.`,
              options: {
                type: "error",
                duration: 4000,
                customIcon: (
                  <Avatar size="xs" shape="square">
                    <AvatarFallback color="danger" shape="square">
                      <Icon type="fas" name="exclamation-triangle" />
                    </AvatarFallback>
                  </Avatar>
                ),
              },
            });
          },
        },
      );
    }
  };

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        id: "name",
        header: "NOMBRE",
        meta: {
          className: "w-[350px] min-w-[300px] max-w-[350px]",
        },
        cell: (info) => {
          const { name, description, files } = info.row.original;
          const imageUrl =
            files && files.length > 0 ? files[files.length - 1].url : null;
          return (
            <div className="flex items-center gap-3">
              <Avatar size="xs" shape="square">
                <AvatarImage src={imageUrl || ""} alt={name} />
                <AvatarFallback color="neutral" shape="square" type="icon">
                  <Icon type="fas" name="tag" />
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col justify-center gap-0 truncate min-w-0">
                <span
                  className="text-body-loud font-semibold truncate"
                  title={name}
                >
                  {name}
                </span>
                <span
                  className="text-body-quiet text-xs leading-5 tracking-wider truncate"
                  title={
                    description ||
                    "Veniam voluptatum a necessitatibus. Cupiditate error tot..."
                  }
                >
                  {description ||
                    "Veniam voluptatum a necessitatibus. Cupiditate error tot..."}
                </span>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("processes_count", {
        id: "processes_count",
        header: "NÚMERO DE PROCESOS",
        meta: {
          className: "hidden md:table-cell",
        },
        cell: (info) => {
          const count = info.getValue();
          return (
            <div className="text-body text-base font-normal leading-7">
              {count} Proceso{count !== 1 ? "s" : ""}
            </div>
          );
        },
      }),
      columnHelper.accessor("active", {
        id: "active",
        header: "ESTATUS",
        meta: {
          className: "hidden md:table-cell",
        },
        cell: (info) => {
          const status = mapActiveToDisplay(info.getValue());
          const { processes_count } = info.row.original;
          const hasProcesses = processes_count > 0;

          if (status === "Activa") {
            // Activas siempre verdes
            return (
              <Badge variant="success" shape="pill" size="sm">
                Activa
              </Badge>
            );
          }
          if (status === "Inactiva") {
            // Inactivas en naranja si tiene procesos asignados
            if (hasProcesses) {
              return (
                <Badge variant="warning" shape="pill" size="sm">
                  Inactiva
                </Badge>
              );
            }
            // Inactivas en gris si no tienen procesos asignados
            return (
              <Badge variant="neutral" shape="pill" size="sm">
                Inactiva
              </Badge>
            );
          }
          return "-";
        },
      }),
      columnHelper.accessor("updated_at", {
        id: "updated_at",
        header: "ÚLTIMA ACTUALIZACIÓN",
        meta: {
          className: "hidden sm:table-cell",
        },
        cell: (info) => {
          const date = info.getValue();
          return date ? (
            <div className="text-body text-base font-normal leading-7">
              {dayjs(date).format("MMM DD, YYYY | HH:mm A")}
            </div>
          ) : (
            "-"
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          className: "w-[56px]",
        },
        cell: (info) => {
          return (
            <div className="w-full flex justify-end">
              <Menu
                tag={info.row.original}
                onEdit={handleEditTag}
                onToggleStatus={handleToggleStatus}
                onDelete={handleDeleteTag}
                onViewHistory={handleViewHistoryTag}
              />
            </div>
          );
        },
      }),
    ].filter(({ id }) => {
      if (!canEdit && !canDelete) return id !== "actions";
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canEdit,
    canDelete,
    handleEditTag,
    handleToggleStatus,
    handleDeleteTag,
    handleCellButtonClick,
    handleViewHistoryTag,
  ]);

  const onApplyFilters = (newFilters: FilterOption[]): FilterOption[] => {
    newFilters.forEach((f) => {
      if (f.isUpdated) {
        if (f.field === "updated_at") {
          setUpdatedAt(f.result || null);
        }
        if (f.field === "status") {
          setStatus(f.result || null);
        }
        setPage(1);
      }
    });

    // Set filter to updated false if result is empty
    const updatedFilters = newFilters.map((f) => {
      if (f.result === "") {
        return {
          ...f,
          isUpdated: false,
          result: "",
        };
      }
      return f;
    });
    return updatedFilters;
  };

  const onCloseFilters = (type: string) => {
    if (type !== "clean") {
      setModalState("idle");
      return;
    }
    setFormatFilters([
      {
        id: 1,
        field: "updated_at",
        type: filterTypesEnum.dates,
        label: "Fecha de última actualización",
        shouldDisabledFutureDates: true,
        isUpdated: false,
        result: "",
      },
      {
        id: 2,
        field: "status",
        type: filterTypesEnum.status,
        label: "Estado de la etiqueta",
        isUpdated: false,
        result: "",
        status: [
          {
            field: "Activa",
            label: "Activa",
            value: false,
          },
          {
            field: "Inactiva",
            label: "Inactiva",
            value: false,
          },
        ],
      },
    ]);
    setUpdatedAt(null);
    setStatus(null);
    setModalState("idle");
  };

  return (
    <>
      {modalState === "filters" && (
        <FiltersModal
          isOpen={modalState === "filters"}
          onToggle={onCloseFilters}
          hasCurrentFilters={formatFilters.some((filter) => filter.isUpdated)}
          items={formatFilters}
          onApply={(newFilters) => {
            const filters = onApplyFilters(newFilters);
            setFormatFilters(filters);
            setModalState("idle");
          }}
        />
      )}

      <div className="flex flex-col gap-3">
        <p className="font-normal text-body mt-8 leading-7 text-base">
          Gestiona las etiquetas asignadas a los procesos.
        </p>
        <div className="relative">
          <div className="flex flex-col bg-neutral-object border-neutral border-[0.5px] rounded-xl shadow-custom overflow-hidden">
            <TableHeader
              createLabel="Nueva etiqueta"
              search={searchInput}
              setSearch={setSearchInput}
              title="Lista de etiquetas"
              isLoading={isFetching}
              canCreate={canCreate}
              onCreate={() => {
                setIsNewTagModalOpen(true);
              }}
              onFilter={() => setModalState("filters")}
              filtersApplied={formatFilters.filter((f) => f.isUpdated).length}
            />
            <TableData
              columns={columns as ColumnDef<TagType>[]}
              data={data?.data || []}
              sorting={sorting}
              onSortingChange={(val: SortingState) => {
                setSorting(val);
                if (val.length === 0) {
                  setOrderBy("!created_at");
                  return;
                }
                setOrderBy(
                  val.length > 0
                    ? `${val[0]?.desc ? "!" : ""}${val[0].id}`
                    : "!created_at",
                );
              }}
              isFirstLoading={shouldShowSkeleton}
              stripped
            />
            <div className="p-3">
              {data?.meta && (
                <Pagination
                  currentPage={data.meta.current_page}
                  totalItems={data.meta.total}
                  hasNext={data.meta.current_page < data.meta.last_page}
                  hasPrevious={data.meta.current_page > 1}
                  onNext={() =>
                    setPage(Math.min(page + 1, data.meta.last_page))
                  }
                  onPageChange={setPage}
                  onPrevious={() => setPage(Math.max(page - 1, 1))}
                  pageSize={20}
                  totalPages={data.meta.last_page}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmación para eliminar */}
      <ConfirmationModalBase
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onAccept={confirmDelete}
        title="Eliminar etiqueta"
        description={`¿Estás seguro de que deseas eliminar la etiqueta "${selectedTag?.name}"?`}
        acceptText="Eliminar"
        cancelText="Cancelar"
        acceptVariant="danger-primary"
        isLoading={deleteMutation.isPending}
      />

      {/* Modal de confirmación para activar etiqueta */}
      <ConfirmationModalBase
        isOpen={isActivateTagModalOpen}
        onClose={() => setIsActivateTagModalOpen(false)}
        onAccept={confirmToggleStatus}
        title="Activar etiqueta"
        description="¿Estás seguro de que deseas activar esta etiqueta? los procesos que estén asignados a esta etiqueta serán visibles en el portal ciudadano."
        acceptText="Activar"
        cancelText="Cancelar"
        acceptVariant="default"
        isLoading={updateMutation.isPending}
      />

      {/* Modal de confirmación para desactivar etiqueta */}
      <ConfirmationModalBase
        isOpen={isDeactivateTagModalOpen}
        onClose={() => setIsDeactivateTagModalOpen(false)}
        onAccept={confirmToggleStatus}
        title="Desactivar etiqueta"
        description="¿Estás seguro de que deseas desactivar esta etiqueta? Los procesos que aún estén asignados a esta etiqueta no estarán visibles en el portal ciudadano."
        acceptText="Desactivar"
        cancelText="Cancelar"
        acceptVariant="danger-tertiary"
        isLoading={updateMutation.isPending}
      />

      {/* Modal para crear nueva etiqueta */}
      {isNewTagModalOpen && (
        <NewTagModal
          open={isNewTagModalOpen}
          onClose={handleCloseNewTagModal}
          onSuccess={handleTagSuccess}
        />
      )}

      {/* Modal para editar etiqueta */}
      {isEditTagModalOpen && selectedTag && (
        <NewTagModal
          open={isEditTagModalOpen}
          onClose={handleCloseEditTagModal}
          onSuccess={handleTagEditSuccess}
          editData={selectedTag || undefined}
        />
      )}
    </>
  );
};

export default TagsList;
